'use client'
import { FC, ChangeEvent, InputHTMLAttributes, ReactNode } from 'react'
import { FieldValues, UseFormRegister } from 'react-hook-form'
import cn from 'classnames'
import { VALIDATION_MESSAGES } from '@/root/modules/shared/ConfirmPage'

interface GeneralInputProps {
  id: string
  label?: string
  type?: InputHTMLAttributes<HTMLInputElement>['type']
  placeholder?: string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  hasError: boolean
  errorMessage?: ReactNode
  requiredErrorText?: string
  validationPattern: RegExp
  validationErrorText?: string
  register: UseFormRegister<FieldValues>
  onBlur?: () => void
  readOnly?: boolean
  showRequired?: boolean
  testId?: string
}

export const GeneralInput: FC<GeneralInputProps> = ({
  label,
  id,
  type = 'text',
  placeholder,
  onChange,
  hasError = false,
  errorMessage,
  requiredErrorText = VALIDATION_MESSAGES.REQUIRED,
  validationPattern,
  validationErrorText = VALIDATION_MESSAGES.INVALID_FORMAT,
  register,
  onBlur,
  readOnly,
  showRequired = false,
  testId,
}) => {
  return (
    <div className="flex w-full flex-col gap-1.5">
      {label && (
        <label htmlFor={id} className="text-neutral-darker text-sm leading-none">
          {label}
          {showRequired && <span className="text-error"> *</span>}
        </label>
      )}
      <input
        id={id}
        data-testid={testId}
        readOnly={readOnly}
        className={cn(
          'w-full rounded-lg border bg-white px-2.5 py-1.5 text-base leading-none',
          'placeholder:text-neutral-medium-lighter placeholder:font-normal',
          'focus:border-primary-base focus:outline-none',
          hasError ? 'border-error' : 'border-neutral-medium-lighter',
          readOnly && 'bg-neutral-very-light cursor-not-allowed'
        )}
        type={type}
        placeholder={placeholder}
        {...register(id, {
          required: requiredErrorText,
          pattern: {
            value: validationPattern,
            message: validationErrorText,
          },
          onBlur: onBlur,
        })}
        onChange={onChange}
      />
      {hasError && <span className="text-error text-xs">{errorMessage}</span>}
    </div>
  )
}

export default GeneralInput
